import React, {useState} from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  Typography
} from '@mui/material';
import {ArrowBack, ErrorOutline, History, Search} from '@mui/icons-material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import {t} from 'ttag';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {useMask} from '@react-input/mask';
import {styles} from '@invoice-system';
import useFadeNavigate from './useFadeNavigate';
import {useGetTicketInfoMutation} from '../../store/apis/ccs';
import {useLocation} from 'react-router-dom';

const RECENT_SEARCHES_KEY = 'ticketUsageRecentSearches';

type RecentSearch = {label: string; ticket: any};

const loadRecents = (): RecentSearch[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) ?? '[]');
  } catch {
    return [];
  }
};

const saveRecent = (label: string, ticket: any) => {
  try {
    const prev = loadRecents().filter((r) => r.label !== label);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify([{label, ticket}, ...prev].slice(0, 5)));
  } catch {}
};

const validationSchema = yup.object().shape({
  ticketNumber: yup
    .string()
    .required(t`Ticket number is required`)
    .matches(/^\d{3}-\d{10}$/, t`Must be in format XXX-XXXXXXXXXX (e.g. 153-1234567890)`)
});

const TicketUsageDeclarationPage = () => {
  const navigate = useFadeNavigate();
  const location = useLocation();
  const existingTickets = (location.state as any)?.existingTickets ?? [];
  const inputRef = useMask({mask: '___-__________', replacement: {_: /\d/}});
  const [getTicketInfo, {isLoading, error, reset}] = useGetTicketInfoMutation();
  const [recents, setRecents] = useState<RecentSearch[]>(() => loadRecents());

  const {control, handleSubmit} = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {ticketNumber: ''}
  });

  const handleSubmitForm = handleSubmit(
    (data) => {
      reset();
      const [company, number] = data.ticketNumber.split('-');
      getTicketInfo({company, number}).then(({data: ticketData}: any) => {
        if (ticketData) {
          const ticket = {...ticketData, company, number, documentType: 'CC', idNumber: '', nif: ''};
          saveRecent(data.ticketNumber, ticket);
          setRecents(loadRecents());
          navigate('/declarations/ticket-usage/result', {
            state: {tickets: [...existingTickets, ticket]}
          });
        }
      });
    },
    (err) => console.error(err)
  );

  return (
    <Card elevation={3}>
      <CardHeader
        title={
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <ConfirmationNumberIcon sx={{color: 'text.primary'}} />
            <Typography sx={styles.title}>{t`Search Electronic Ticket`}</Typography>
          </Box>
        }
        subheader={
          <Typography sx={styles.subHeader} variant='subtitle2'>
            {t`Enter the ticket number (13 digits, e.g. 153-1234567890).`}
          </Typography>
        }
      />
      <Divider />
      <CardContent sx={{py: 4}}>
        <Controller
          name='ticketNumber'
          control={control}
          render={({field, fieldState: {error: fieldError}}) => (
            <TextField
              {...field}
              inputRef={inputRef}
              label={t`Ticket Number`}
              placeholder='153-1234567890'
              variant='outlined'
              fullWidth
              size='small'
              error={!!fieldError}
              helperText={fieldError ? fieldError.message : ''}
              slotProps={{inputLabel: {shrink: true}}}
            />
          )}
        />
        {error && (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'error.main'}}>
            <ErrorOutline fontSize='small' />
            <Typography variant='body2'>{t`Ticket not found. Please check the number and try again.`}</Typography>
          </Box>
        )}

        {recents.length > 0 && (
          <Box sx={{mt: 3}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 1}}>
              <History sx={{fontSize: '0.9rem', color: 'text.disabled'}} />
              <Typography variant='caption' color='text.secondary' sx={{fontWeight: 700, letterSpacing: '0.06em'}}>
                {t`RECENT SEARCHES`}
              </Typography>
            </Box>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75}}>
              {recents.map((r) => (
                <Chip
                  key={r.label}
                  label={r.label}
                  size='small'
                  variant='outlined'
                  icon={<ConfirmationNumberIcon sx={{fontSize: '0.85rem !important'}} />}
                  onClick={() =>
                    navigate('/declarations/ticket-usage/result', {state: {tickets: [r.ticket]}})
                  }
                  sx={{fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.02em'}}
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
      <Divider />
      <CardActions sx={{display: 'flex', justifyContent: 'space-between', m: 1}}>
        <Button variant='text' startIcon={<ArrowBack />} sx={{color: 'text.secondary'}} onClick={() => navigate('/declarations')}>
          {t`Back`}
        </Button>
        <Button
          variant='contained'
          color='primary'
          startIcon={isLoading ? <CircularProgress size={16} color='inherit' /> : <Search />}
          onClick={handleSubmitForm}
          disabled={isLoading}
        >
          {isLoading ? t`Searching...` : t`Search`}
        </Button>
      </CardActions>
    </Card>
  );
};

export default TicketUsageDeclarationPage;
